<template>
  <div class="file-dropzone">
    <!-- Detached from parent <form> so file pick / Enter cannot submit the profile form -->
    <input
      ref="fileInputRef"
      type="file"
      class="file-dropzone__input"
      form=""
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled"
      tabindex="-1"
      aria-hidden="true"
      @change="onInputChange"
      @click.stop
    />

    <div
      class="pm-dropzone h-full"
      :class="{
        'pm-dropzone-active': isDragging,
        'pm-dropzone--disabled': disabled
      }"
      role="button"
      tabindex="0"
      @click.stop.prevent="triggerChoose"
      @keydown.enter.stop.prevent="triggerChoose"
      @keydown.space.stop.prevent="triggerChoose"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent.stop="handleDrop"
    >
      <i class="pi pi-cloud-upload text-xl" aria-hidden="true" />
      <div class="text-xs">
        Drag files here or <span class="underline underline-offset-2">click to browse</span>
      </div>
      <div class="pm-drop-hint">{{ dropzoneHintText }}</div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const DEFAULT_MAX_SIZE = 2147483648;

const props = defineProps({
  disabled: { type: Boolean, default: false },
  accept: {
    type: String,
    default: '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt'
  },
  maxFileSize: { type: Number, default: DEFAULT_MAX_SIZE },
  dropzoneHint: { type: String, default: '' },
  multiple: { type: Boolean, default: true }
});

const emit = defineEmits(['select']);

const fileInputRef = ref(null);
const isDragging = ref(false);

const dropzoneHintText = computed(() => {
  if (props.dropzoneHint) return props.dropzoneHint;
  const max = props.maxFileSize;
  if (max >= 1024 * 1024 * 1024) return 'Up to 2GB per file';
  if (max >= 1024 * 1024) return `Up to ${Math.round(max / (1024 * 1024))}MB per file`;
  if (max >= 1024) return `Up to ${Math.round(max / 1024)}KB per file`;
  return `Up to ${max} bytes per file`;
});

const allowedExtensions = computed(() =>
  (props.accept || '')
    .split(',')
    .map((part) => part.trim().replace(/^\./, '').toLowerCase())
    .filter(Boolean)
);

const isAllowedExtension = (fileName) => {
  const allowed = allowedExtensions.value;
  if (!allowed.length) return true;
  const ext = String(fileName || '').split('.').pop()?.toLowerCase() || '';
  return allowed.includes(ext);
};

const validateFiles = (incomingFiles) => {
  const valid = [];
  const errors = [];

  for (const file of incomingFiles) {
    if (!isAllowedExtension(file.name)) {
      errors.push(`File type not allowed: ${file.name}`);
      continue;
    }
    if (file.size > props.maxFileSize) {
      errors.push(`File ${file.name} is too large (${dropzoneHintText.value})`);
      continue;
    }
    valid.push(file);
  }

  return { valid, errors };
};

const emitSelection = (incomingFiles) => {
  if (props.disabled || !incomingFiles.length) return;
  const { valid, errors } = validateFiles(incomingFiles);
  emit('select', { files: valid, errors });
};

const resetInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const triggerChoose = () => {
  if (props.disabled) return;
  fileInputRef.value?.click();
};

const onInputChange = (event) => {
  const selected = Array.from(event.target.files || []);
  if (selected.length) emitSelection(selected);
  resetInput();
};

const handleDragOver = (event) => {
  event.preventDefault();
  if (props.disabled) return;
  isDragging.value = true;
};

const handleDragLeave = (event) => {
  event.preventDefault();
  isDragging.value = false;
};

const handleDrop = (event) => {
  event.preventDefault();
  isDragging.value = false;
  if (props.disabled) return;
  const dropped = Array.from(event.dataTransfer?.files || []);
  if (dropped.length) emitSelection(dropped);
};

defineExpose({
  choose: triggerChoose,
  clear: resetInput
});
</script>

<style scoped>
.file-dropzone {
  position: relative;
}

.file-dropzone__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
}

.pm-dropzone {
  min-height: 137px;
  border: 2px dashed var(--p-content-border-color, #e5e7eb);
  border-radius: 12px;
  background: var(--p-content-background, var(--surface-card, #fff));
  color: var(--p-text-secondary-color, #6b7280);
  display: grid;
  place-items: center;
  gap: 0.25rem;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  cursor: pointer;
}

.pm-dropzone:hover:not(.pm-dropzone--disabled) {
  border-color: var(--p-primary-color);
  background: rgba(var(--p-primary-500-rgb, 39, 126, 50), 0.05);
  color: var(--p-primary-color);
}

.pm-dropzone-active {
  border-color: var(--p-primary-color);
  background: rgba(var(--p-primary-500-rgb, 39, 126, 50), 0.08);
  color: var(--p-primary-color);
}

.pm-dropzone--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pm-drop-hint {
  font-size: 0.7rem;
  opacity: 0.8;
  color: var(--p-text-secondary-color, #6b7280);
}
</style>
